import { Button, Card, CardActions, CardContent, CardHeader, Container, Box, Typography, Paper, Grid, Alert } from "@mui/material"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { useNavigate, useParams } from "react-router"
import { QueryClient, useMutation, useQuery } from "@tanstack/react-query"
import { deleteEvent, getEvent } from "../../controllers/event/event"
import { useContext, useEffect } from "react"
import { UserContext } from "../../context/UserContext"
import { deleteSinglePost } from "../../controllers/posts/posts"
import { Warning, ArrowBack, Delete, Event } from "@mui/icons-material"

export const DeleteEvent = () => {
    const { id } = useParams()
    let navigate = useNavigate()    
    const { userDetails } = useContext(UserContext)
    let token = userDetails?.token
    const { data:event, refetch } = useQuery({ queryKey: ['getSingleEvent'], queryFn: () => getEvent(token, id) })
    let single_event = event?.event ?? {}
    useEffect(() => {
        refetch()
    })
    
    const queryClient = new QueryClient()
    const mutation = useMutation({
        mutationFn: deleteEvent,
        onSuccess: () => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['deleteEvent'] })
        navigate('/event/list')
        },
    })
    const mutationMastodonDelete = useMutation({
        mutationFn: deleteSinglePost,
        onSuccess: () => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['deleteSinglePost'] })
        },
    })

    const deleteEvnt = () => {
        // delete mastodon status item
        mutationMastodonDelete.mutate({
            id:single_event?.status,
            token,
        })

        // delete mongodb event item
        mutation.mutate({
          id,
          token,
          status_id: single_event?.status
        })
    }
    return (
        <MainLoggedInLayout>
            {single_event?.author?.includes(userDetails?.id) ? (
                <Container maxWidth="md" sx={{ py: 4 }}>
                    <Card sx={{ 
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                        border: '2px solid',
                        borderColor: 'error.light'
                    }}>
                        <CardHeader 
                            avatar={
                                <Warning sx={{ color: 'error.main', fontSize: 40 }} />
                            }
                            title={
                                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                    Delete Event
                                </Typography>
                            }
                            subheader={
                                <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                                    This action cannot be undone
                                </Typography>
                            }
                            sx={{ pb: 1 }}
                        />
                        
                        <CardContent sx={{ p: 4 }}>
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    Warning: This is a permanent action
                                </Typography>
                            </Alert>

                            <Paper sx={{ p: 3, backgroundColor: '#fff3e0', border: '1px solid #ffcc02' }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                    You are about to delete:
                                </Typography>
                                
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        Event: {single_event?.title || 'Event'}
                                    </Typography>
                                    {single_event?.start_date_time && (
                                        <Typography variant="body2" color="text.secondary">
                                            Date: {new Date(single_event.start_date_time).toDateString()}
                                        </Typography>
                                    )}
                                    {single_event?.location?.formatted_address && (
                                        <Typography variant="body2" color="text.secondary">
                                            Location: {single_event.location.formatted_address}
                                        </Typography>
                                    )}
                                    {single_event?.category && (
                                        <Typography variant="body2" color="text.secondary">
                                            Category: {single_event.category}
                                        </Typography>
                                    )}
                                    {single_event?.RSVP?.length > 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                            RSVPs: {single_event.RSVP.length} people attending
                                        </Typography>
                                    )}
                                </Box>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    This will permanently delete:
                                </Typography>
                                
                                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        The event and all its details
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        All RSVP information and attendee data
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        Event description and media content
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        Associated social media posts
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        Event history and analytics
                                    </Typography>
                                </Box>
                            </Paper>

                            <Alert severity="warning" sx={{ mt: 3 }}>
                                <Typography variant="body2">
                                    <strong>Important:</strong> Attendees will no longer be able to access event information. 
                                    Consider notifying participants before deletion if the event has RSVPs.
                                </Typography>
                            </Alert>
                        </CardContent>
                        
                        <CardActions sx={{ p: 3, backgroundColor: '#fafafa' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Button 
                                        fullWidth 
                                        variant="outlined" 
                                        startIcon={<ArrowBack />}
                                        onClick={() => navigate("/event/show/"+single_event?._id)}
                                        sx={{ 
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Cancel & Go Back
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Button 
                                        fullWidth 
                                        variant="contained" 
                                        color="error"
                                        startIcon={<Delete />}
                                        onClick={deleteEvnt}
                                        sx={{ 
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            '&:hover': {
                                                backgroundColor: 'error.dark'
                                            }
                                        }}
                                    >
                                        Yes, Delete Forever
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardActions>
                    </Card>
                </Container>
            ) : (
                <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                    <Paper sx={{ p: 4, borderRadius: 3 }}>
                        <Typography variant="h5" color="error" gutterBottom>
                            Access Denied
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            You don't have permission to delete this event. Only the event organizer can delete their events.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={() => navigate("/event/list")}
                            sx={{ 
                                textTransform: 'none',
                                fontWeight: 'bold',
                                borderRadius: 2
                            }}
                        >
                            Browse Events
                        </Button>
                    </Paper>
                </Container>
            )}
        </MainLoggedInLayout>
    )
}